import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
// components
import { FormattedMessage } from 'react-intl';
import TreatmentProtocolProductsTable from '../../components/TreatmentProtocolProductsTable';
import Preloader from 'components/Preloader';
import MessageBox from 'components/MessageBox';
import { Link } from 'react-router';
import TreatmentProtocolProfileHeader from '../../components/TreatmentProtocolProfileHeader';
// endpoints
import {
  getPigGroupTreatmentProtocol,
  updatePigGroupTreatmentProtocol,
  getTreatmentProtocolsFarm,
} from 'endpoints/treatmentProtocols';
// utils
import { connect } from 'react-redux';
import { toastResponseErrors } from 'utils/responseErrorsHelper';

const GroupTreatmentProtocolProfile = ({
  router,
  location: { pathname },
  params: { protocolId, farmId, groupId },
  setPageOptions,
  setBreadcrumbs,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [treatmentProtocol, setTreatmentProtocol] = useState(null);
  const [farm, setFarm] = useState(null);
  const farmName = farm?.name || '';
  const pigGroupName = treatmentProtocol?.pig_group?.name || '';

  const fetchData = async () => {
    try {
      setTreatmentProtocol(await getPigGroupTreatmentProtocol(groupId, protocolId));
      setFarm(await getTreatmentProtocolsFarm(farmId));
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  // Read back through show: the status decides which copies the protocol is combined with
  const onTreatmentProtocolStatusChange = async (isActive) => {
    try {
      setIsLoading(true);
      const updated = await updatePigGroupTreatmentProtocol(groupId, protocolId, { active: isActive });
      // The change is saved either way; a failed read back only costs the combined notice
      setTreatmentProtocol(await getPigGroupTreatmentProtocol(groupId, protocolId).catch(() => updated));
    } catch (error) {
      toastResponseErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPageOptions({
      backLink: `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`
    });
    setBreadcrumbs([
      { label: <FormattedMessage id="general.treatmentProtocolFarms" />, path: '/treatment-protocols-farms' },
      { label: farmName, path: `/treatment-protocols-farms/${farmId}/treatment-protocols` },
      {
        label: pigGroupName,
        path: `/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols`,
      },
      { label: treatmentProtocol?.name || '', useLabelAsMobileTitle: true },
    ]);
  }, [pigGroupName, treatmentProtocol?.name, farmName, farmId, groupId]);

  if (!treatmentProtocol) {
    return <Preloader isActive />;
  }

  const options = [
    {
      label: <FormattedMessage id="general.button.edit" />,
      onClick: () => router.push(`${pathname}/edit`),
    },
    {
      label: <FormattedMessage id={`general.button.${treatmentProtocol.active ? 'disable' : 'enable'}`} />,
      onClick: () => onTreatmentProtocolStatusChange(!treatmentProtocol.active), // passing reversed value
    },
  ];

  const combinedEntry = treatmentProtocol.combined_entry;
  // Copies often share a name, the sow unit tells them apart
  const getCombinedNames = (protocols) => [...new Set(protocols.map(({ name, source_farm }) => (
    source_farm?.name ? `${name} (${source_farm.name})` : name
  )))].join(' / ');

  return (
    <div>
      <Preloader isActive={isLoading} />
      <TreatmentProtocolProfileHeader
        treatmentProtocol={treatmentProtocol}
        options={options}
        showEffectiveDates={!!treatmentProtocol.farm_treatment_protocol}
      />
      {combinedEntry && (
        // The group list shows this copy with copies of other sow units; changes here stay on this one
        <div className="pl-15 pr-15 mt-15">
          <MessageBox type="info">
            <FormattedMessage
              id="component.treatmentProtocols.combinedCopyNotice"
              values={{
                names: getCombinedNames(combinedEntry.protocols),
                b: (msg) => <b>{msg}</b>,
              }}
            />
            {combinedEntry.fully_identical && (
              <Link className="primary ml-5" to={`${pathname}/edit?sync_combined=true`}>
                <FormattedMessage id="component.treatmentProtocols.editCombinedTogether" />
              </Link>
            )}
          </MessageBox>
        </div>
      )}
      <TreatmentProtocolProductsTable treatmentProducts={treatmentProtocol.treatment_protocol_products} />
    </div>
  );
};

GroupTreatmentProtocolProfile.propTypes = {
  router: T.object.isRequired,
  location: T.object.isRequired,
  params: T.object.isRequired,
  setPageOptions: T.func.isRequired,
  setBreadcrumbs: T.func.isRequired,
};

export default connect(null, {
  setPageOptions,
  setBreadcrumbs,
})(GroupTreatmentProtocolProfile);
